import DateTime from 'luxon/src/datetime'
import Base from './base'
import api from '../api/v1'

const ORDER_FIELDS = {
  driver: 'driver_full_name'
}

const formatDecimal = (value) => {
  if (value === null || value === undefined || value === '') return null
  const number = Number(value)
  if (!Number.isFinite(number)) return null
  return number.toLocaleString('pl-PL', { maximumFractionDigits: 2 })
}

const formatDuration = (totalSeconds) => {
  if (totalSeconds === null || totalSeconds === undefined || totalSeconds === '') return null
  const seconds = Number(totalSeconds)
  if (!Number.isFinite(seconds)) return null
  const rounded = Math.round(seconds)
  const hours = Math.floor(rounded / 3600)
  const minutes = Math.floor((rounded % 3600) / 60)
  const remainingSeconds = Math.abs(rounded % 60)
  return `${hours}:${String(minutes).padStart(2, '0')}:${String(remainingSeconds).padStart(2, '0')}`
}

const formatKilometers = (kilometers) => {
  const formatted = formatDecimal(kilometers)
  return formatted && `${formatted} km`
}

const monthDateRange = (dateRange) => {
  const start = DateTime.fromISO(dateRange?.[0] || DateTime.local().toISODate())
  const endSource = dateRange?.[1] ? DateTime.fromISO(dateRange[1]) : start
  return [
    start.startOf('month').toISODate(),
    endSource.endOf('month').toISODate()
  ]
}

const mapSummary = (summary = {}) => ({
  averageLoops: formatDecimal(summary.avgLoopCount),
  averageLoopLength: formatKilometers(summary.avgLoopLength),
  containerRotation: formatDecimal(summary.containerRotation),
  averageLoopTime: formatDuration(summary.avgLoopTime),
  driversCount: summary.activeDriversCount == null ? null : String(summary.activeDriversCount),
  averageWorkTime: formatDuration(summary.avgWorkTime),
  absoluteAverageLoops: formatDecimal(summary.avgAbsoluteLoopCount),
  containersCount: summary.containersCount == null ? null : String(summary.containersCount)
})

const mapDriver = (driver) => ({
  id: driver.id,
  driver: driver.driverFullName,
  averageLoopLength: formatKilometers(driver.avgLoopLength),
  averageLoopTime: formatDuration(driver.avgLoopTime),
  averageWorkTime: formatDuration(driver.avgWorkTime),
  averageLoops: formatDecimal(driver.avgLoopCount),
  absoluteLoops: formatDecimal(driver.absoluteLoopCount),
  vehicleType: driver.vehicleType
})

const base = new Base('driversSummary')

const state = {
  vehicleType: null,
  summary: null
}

const mutations = {
  SET_VEHICLE_TYPE (state, vehicleType) {
    state.vehicleType = vehicleType || null
  },
  SET_SUMMARY (state, summary) {
    state.summary = summary
  }
}

const actions = {
  getItems ({ commit, state, rootState, rootGetters }) {
    commit('SET_PROCESSING', true)

    const tableConfig = rootGetters['tables/getTableConfig']('driversSummary')
    const sortBy = tableConfig?.sorting?.sortBy || 'driver'
    const sortDesc = tableConfig?.sorting?.sortDesc
    const query = (tableConfig?.search || '').trim()
    const departmentId = rootState.core.department?.id

    const filters = {
      daterange: monthDateRange(state.dateRange)
    }
    if (departmentId) filters.departmentIds = [departmentId]
    if (query) filters.query = query
    if (state.vehicleType) filters.vehicleType = state.vehicleType

    const listParams = {
      endpoint: 'reports/drivers',
      filters,
      order: {
        field: ORDER_FIELDS[sortBy] || ORDER_FIELDS.driver,
        direction: sortDesc ? 'desc' : 'asc'
      }
    }

    return Promise.all([
      api.getItems({ tableName: 'driversSummary', params: listParams }),
      api.getItems({ tableName: 'driversSummary', params: { endpoint: 'reports/drivers_summary', filters } })
    ])
      .then(([listResponse, summaryResponse]) => {
        const items = (listResponse.data?.drivers || []).map(mapDriver)
        commit('SET_ITEMS', { items })
        commit('SET_ITEMS_COUNT', items.length)
        commit('SET_SUMMARY', mapSummary(summaryResponse.data))
      })
      .finally(() => {
        commit('SET_PROCESSING', false)
      })
  },
  setVehicleType ({ commit, dispatch }, vehicleType) {
    commit('SET_VEHICLE_TYPE', vehicleType)
    return dispatch('getItems')
  }
}

base.state = {
  ...base.state,
  ...state
}
base.mutations = {
  ...base.mutations,
  ...mutations
}
base.actions = {
  ...base.actions,
  ...actions
}

export default base
